/**
 * A class encapsulating a three.js scene with our additions like light probes and clipping state management.
 * @module
 */
import type { Table } from "common/data/table";
import { assertIsRecordWithKeys } from "common/utils/assertOfType";
import type { AABB2 } from "geom/aabb2Types";
import type { PlaneBSP } from "geom/bspTypes";
import { planeBSPByPointAndNormal } from "geom/planeBSP";
import * as THREE from "three";
import type { ToneMapping } from "three";
import { planeBSPToTHREEClippingPlane } from "../../convert";
import { applyDefaultPhysicalSceneSettings } from "../../defaultSceneSettings";
import { CLIPPING_LAYER, UI_LAYER } from "../../layerConstants";
import { prepareMaterialsForRender } from "../../materials/prepareMaterialsForRender";
import { pickingFragmentShader } from "../../materials/shaders/picking";
import { SceneLightsManager } from "../../SceneLightsManager";
import { getPmremGenerator } from "../../sharedPmremGenerator";
import { TONE_MAPPING_ALGORITHM } from "../../toneMappingConstants";
import type {
  ExtendedClippingPlane,
  ExtendedObject3D,
  ExtendedScene,
  ExtendedWebGLRenderer,
} from "../three-extensions";
import { checkWebGLContextLost } from "./checkWebGLContextLost";
import { SSFXRendererLayerUniforms } from "./LayerSpecificUniforms";
import type { LightProbeGenerationParams } from "./LightProbeGenerationParams";
import {
  getEmptyHighlightTexture,
  MaterialSharedParameterGroup,
  PROBE_GRID_NORMAL_OFFSET_MULTIPLIER_DEFAULT,
  PROBE_GRID_SMOOTH_BACKFACE_TERM_CONSTANT_TERM_DEFAULT,
  SOFT_SHADOW_CAMERA_FAR_DEFAULT,
  type SharedUniformValues,
} from "./MaterialSharedParameterGroup";
import { NDCBoundingBox } from "./NDCBoundingBox";
import { RefractionProbePositionAllocation } from "./RefractionProbePositionAllocation";
import { RenderParamsSource } from "./RenderParamsSource";
import { SHLightProbeGrid } from "./SHLightProbeGrid";
import { SSFXRendererOutput } from "./SSFXRendererOutput";
import type { RendererParamOverrides, SSFXRendererSceneContainer } from "./SSFXRendererTypes";
import type { ToneMappingDefines } from "./ToneMappingDefines";
import { setToneMappingDefine, ToneMappingDefineKeys } from "./ToneMappingDefines";

let incrementStencilMat: THREE.MeshBasicMaterial;
let decrementStencilMat: THREE.MeshBasicMaterial;
let clearStencilMat: THREE.MeshBasicMaterial;
let crossSectionMaterial: THREE.MeshBasicMaterial;

// Shared across every renderer and scene so the PMREM conversion in onBeforeRender runs once per
// (renderer, environment texture) pair rather than once per throwaway per-render scene.
const environmentTargets = new WeakMap<THREE.WebGLRenderer, WeakMap<THREE.Texture, THREE.WebGLRenderTarget>>();

const tempVector = new THREE.Vector3();

const sectionGeo = new THREE.PlaneGeometry(5000, 5000);
sectionGeo.userData.isCached = true;

function isLevelGroupVisible(
  levelGroup: THREE.Object3D,
  selectedLevelId: string | undefined,
  levels: Table<{ floorIndex: number }>,
) {
  if (!selectedLevelId) {
    return true;
  }

  if (levelGroup.userData.levelId === undefined) {
    return true;
  }

  if (!levels[levelGroup.userData.levelId]) {
    // This might happen if the number of levels is changing and it's not synchronous with updating the level group objects.
    return true;
  }

  if (!levels[selectedLevelId]) {
    // This might happen if the number of levels is changing and it's not synchronous with updating the level group objects.
    return true;
  }

  return levelGroup.userData.aboveLevel
    ? levelIsBelow(levelGroup.userData.levelId, selectedLevelId, levels)
    : levelIsBelowOrEqual(levelGroup.userData.levelId, selectedLevelId, levels);
}

/** Copied from other package to prevent circ deps */
function levelIsBelow(levelA: string, levelB: string, levels: Table<{ floorIndex: number }>) {
  return levels[levelA].floorIndex < levels[levelB].floorIndex;
}

/** Copied from other package to prevent circ deps */
function levelIsBelowOrEqual(levelA: string, levelB: string, levels: Table<{ floorIndex: number }>) {
  return levels[levelA].floorIndex <= levels[levelB].floorIndex;
}

function initClippingMats() {
  if (!incrementStencilMat) {
    const baseMat = new THREE.MeshBasicMaterial();
    baseMat.depthWrite = false;
    baseMat.depthTest = false;
    baseMat.colorWrite = false;
    baseMat.stencilWrite = true;
    baseMat.stencilFunc = THREE.AlwaysStencilFunc;

    // back faces
    incrementStencilMat = baseMat.clone();
    incrementStencilMat.side = THREE.BackSide;
    incrementStencilMat.stencilFail = THREE.IncrementWrapStencilOp;
    incrementStencilMat.stencilZFail = THREE.IncrementWrapStencilOp;
    incrementStencilMat.stencilZPass = THREE.IncrementWrapStencilOp;

    // front faces
    decrementStencilMat = baseMat.clone();
    decrementStencilMat.side = THREE.FrontSide;
    decrementStencilMat.stencilFail = THREE.DecrementWrapStencilOp;
    decrementStencilMat.stencilZFail = THREE.DecrementWrapStencilOp;
    decrementStencilMat.stencilZPass = THREE.DecrementWrapStencilOp;

    clearStencilMat = baseMat.clone();
    clearStencilMat.stencilRef = 0;
    clearStencilMat.stencilFail = THREE.ReplaceStencilOp;
    clearStencilMat.stencilZFail = THREE.ReplaceStencilOp;
    clearStencilMat.stencilZPass = THREE.ReplaceStencilOp;

    crossSectionMaterial = new THREE.MeshBasicMaterial({
      color: 0x000000,
      stencilWrite: true,
      stencilRef: 0,
      stencilFunc: THREE.NotEqualStencilFunc,
      stencilFail: THREE.ReplaceStencilOp,
      stencilZFail: THREE.ReplaceStencilOp,
      stencilZPass: THREE.ReplaceStencilOp,
    });
  }
}

type SceneSharedUniformValues = Pick<
  SharedUniformValues,
  "highlightedIds" | "radialFogParams" | "ssfxToneMappingEnabled" | "toneMappingExposure" | "isolateMode"
>;

/**
 * Encapsulates a three.js scene together with light probes, highlighted ids and other related scene-specific data.
 */
export default class SSFXRendererScene {
  readonly container?: SSFXRendererSceneContainer;

  readonly scene: THREE.Scene;

  readonly worldUpVector: THREE.Vector3;

  // This can be either a cube map or an equirectangular texture, in which case it will be converted to a cube map at render time.
  environment: THREE.Texture | null = null;

  private shadowGenerationInternal: number = 0;
  private lastRenderedShadowGeneration: number = -1;

  private lastShadowMapRenderer: WeakRef<THREE.WebGLRenderer> | undefined;
  lightProbeGrids: SHLightProbeGrid[] = [];

  get shadowGeneration() {
    return this.shadowGenerationInternal;
  }

  readonly lightOffsetMapTextures: THREE.Texture[];

  readonly layerSpecificUniforms: SSFXRendererLayerUniforms;

  readonly refractionProbeGroup: THREE.Group;

  readonly refractionProbePositionAllocation = new RefractionProbePositionAllocation();

  colorGradingLUT: THREE.Texture | null = null;

  private readonly renderParamOverrides: RendererParamOverrides;

  crossSectionMaterialColor = new THREE.Color();
  private clippingGroup: THREE.Object3D | undefined;
  private clippingPlane: ExtendedClippingPlane;
  private globalClippingPlanes: THREE.Plane[] = [];
  private section: THREE.Mesh | undefined;

  getSection() {
    return this.section;
  }

  // This manages the standard lighting setup in the scene, but the lighting setup is not added to the scene automatically.
  // The user of this class has to call renderSceneLights() and add sceneLightsManager.group to the scene if the lighting setup is desired.
  readonly sceneLightsManager: SceneLightsManager;

  // Incremented whenever a refresh starts and decremented whenever it ends.
  annotationUpdatesInProgress: number = 0;

  asyncProbeUpdater: Iterator<void> | undefined;

  private isRenderingShadowMap: boolean = false;
  isRenderingWireframe: boolean = false;

  usingMeshIdMaterial: boolean = false;

  private isRenderingLightProbeInternal: boolean = false;

  get isRenderingLightProbe(): boolean {
    return this.isRenderingLightProbeInternal;
  }

  private lightProbeUpdateProgressInternal = 0;

  get lightProbeUpdateProgress(): number {
    if (this.asyncProbeUpdater === undefined) {
      return 1.0;
    }
    return this.lightProbeUpdateProgressInternal;
  }

  markShadowMapsForUpdate() {
    ++this.shadowGenerationInternal;
  }

  selectedLevelId: string | undefined;
  levels: Table<{ floorIndex: number }> = {};

  constructor({
    scene,
    layerSpecificUniforms,
    container,
    worldUpVector = new THREE.Vector3(0, 0, 1),
  }: {
    scene: THREE.Scene;
    layerSpecificUniforms?: SSFXRendererLayerUniforms;
    container?: SSFXRendererSceneContainer;
    worldUpVector?: THREE.Vector3;
  }) {
    this.scene = scene;
    this.layerSpecificUniforms = layerSpecificUniforms ?? new SSFXRendererLayerUniforms();
    this.container = container;
    this.worldUpVector = worldUpVector;
    (this.scene as ExtendedScene).getUniformsForLayerMask = this.layerSpecificUniforms.getUniformsForLayerMask;

    this.renderParamOverrides = {
      renderPipelineParams: {},
      lineworkParams: {},
      saoParams: {},
      gtaoParams: {},
      dofParams: {},
      reflectionParams: {},
      lightProbeRenderParams: {},
      id: 0,
      source: RenderParamsSource.SCENE,
    };

    // Real environment maps take a while to load. Ensure shaders will be compiled with env maps, fog etc. enabled form the start.
    applyDefaultPhysicalSceneSettings(this.scene);

    this.lightOffsetMapTextures = [];

    this.scene.onBeforeRender = (renderer, _scene, camera) => {
      this.onBeforeRender(renderer, camera);
    };
    this.scene.onAfterRender = (renderer, _scene, _camera) => {
      this.onAfterRender(renderer);
    };

    this.sceneLightsManager = new SceneLightsManager(this);

    this.refractionProbeGroup = new THREE.Group();
    this.refractionProbeGroup.name = "RefractionProbes";

    this.clippingPlane = new (THREE as any).ClippingPlane(new THREE.Plane(new THREE.Vector3(0, 0, -1), 0));
    this.clippingPlane.layers.disableAll();
  }

  getRenderParams(): RendererParamOverrides {
    return {
      renderPipelineParams: { ...this.renderParamOverrides.renderPipelineParams },
      lineworkParams: { ...this.renderParamOverrides.lineworkParams },
      saoParams: { ...this.renderParamOverrides.saoParams },
      gtaoParams: { ...this.renderParamOverrides.gtaoParams },
      dofParams: { ...this.renderParamOverrides.dofParams },
      reflectionParams: { ...this.renderParamOverrides.reflectionParams },
      screenSpaceShadowParams: { ...this.renderParamOverrides.screenSpaceShadowParams },
      lightProbeRenderParams: { ...this.renderParamOverrides.lightProbeRenderParams },
      source: RenderParamsSource.SCENE,
      id: this.renderParamOverrides.id,
    };
  }

  updateRenderParams(params: Omit<RendererParamOverrides, "id" | "source">) {
    this.renderParamOverrides.renderPipelineParams = {
      ...this.renderParamOverrides.renderPipelineParams,
      ...params.renderPipelineParams,
    };
    this.renderParamOverrides.lineworkParams = {
      ...this.renderParamOverrides.lineworkParams,
      ...params.lineworkParams,
    };
    this.renderParamOverrides.saoParams = { ...this.renderParamOverrides.saoParams, ...params.saoParams };
    this.renderParamOverrides.gtaoParams = { ...this.renderParamOverrides.gtaoParams, ...params.gtaoParams };
    this.renderParamOverrides.dofParams = { ...this.renderParamOverrides.dofParams, ...params.dofParams };
    this.renderParamOverrides.reflectionParams = {
      ...this.renderParamOverrides.reflectionParams,
      ...params.reflectionParams,
    };
    this.renderParamOverrides.screenSpaceShadowParams = {
      ...this.renderParamOverrides.screenSpaceShadowParams,
      ...params.screenSpaceShadowParams,
    };
    this.renderParamOverrides.lightProbeRenderParams = {
      ...this.renderParamOverrides.lightProbeRenderParams,
      ...params.lightProbeRenderParams,
    };

    // Mark as dirty
    ++this.renderParamOverrides.id;
    this.container?.markRenderingNeedsUpdate();
  }

  onAdd() {
    this.markShadowMapsForUpdate();
  }

  onRemove() {
    this.markShadowMapsForUpdate();
  }

  onClear() {
    this.disposeLightOffsetMaps();
    this.clearRefractionProbes();
  }

  removeReferenceFromRefractionProbes(referencingId: string) {
    for (const probePosition of this.refractionProbePositionAllocation.getProbePositions()) {
      probePosition.referencingIds.delete(referencingId);
    }
  }

  removeUnreferencedRefractionProbes() {
    for (const probePosition of [...this.refractionProbePositionAllocation.getProbePositions()]) {
      if (probePosition.referencingIds.size === 0) {
        const probeGrid = probePosition.probeGrid;
        if (probeGrid) {
          this.refractionProbeGroup.remove(probeGrid);
          probeGrid.dispose();
        }
        this.refractionProbePositionAllocation.removeProbePosition(probePosition);
      }
    }
  }

  private clearRefractionProbes() {
    for (const probePosition of this.refractionProbePositionAllocation.getProbePositions()) {
      const probeGrid = probePosition.probeGrid;
      if (probeGrid) {
        this.refractionProbeGroup.remove(probeGrid);
        probeGrid.dispose();
      }
    }
    this.refractionProbePositionAllocation.replaceProbePositionsList([]);
  }

  getNDCBoundingBox(camera: THREE.Camera): AABB2 {
    return NDCBoundingBox(this.scene, camera, (obj) => obj === this.sceneLightsManager.skybox);
  }

  disposeLightOffsetMaps() {
    for (const texture of this.lightOffsetMapTextures) {
      texture.dispose();
    }
    this.layerSpecificUniforms.disposeLightOffsetMaps();
    this.lightOffsetMapTextures.splice(0, this.lightOffsetMapTextures.length);
  }

  updateLightProbesList(): SHLightProbeGrid[] {
    const allGrids: SHLightProbeGrid[] = [];
    this.scene.traverse((obj) => {
      if (obj instanceof SHLightProbeGrid) {
        allGrids.push(obj);
      }
    });
    this.lightProbeGrids = allGrids;
    this.layerSpecificUniforms.updateLightProbeGrids(allGrids, () => this.interruptAsyncProbeUpdate());
    return this.lightProbeGrids;
  }

  dirtyAllLightProbes() {
    this.interruptAsyncProbeUpdate();
    for (const grid of this.lightProbeGrids) {
      grid.probesNeedUpdate = true;
    }
  }

  hasDirtyLightProbes() {
    for (const grid of this.lightProbeGrids) {
      if (grid.probesNeedUpdate) {
        return true;
      }
    }
    return false;
  }

  // Note: don't set the z value here at the same exact height as geometry, or there might be z-fighting issues!
  setClipState(
    meshes1: THREE.Mesh[],
    meshes2: THREE.Mesh[],
    holeMeshes: THREE.Mesh[],
    clippingPlane: PlaneBSP,
    clipEverythingExceptUI: boolean,
    globalClippingPlanes: PlaneBSP[],
    addSectionPoche: boolean,
  ) {
    initClippingMats();
    if (clipEverythingExceptUI) {
      this.clippingPlane.layers.enableAll();
      this.clippingPlane.layers.disable(UI_LAYER);
    } else {
      this.clippingPlane.layers.set(CLIPPING_LAYER);
    }

    planeBSPToTHREEClippingPlane(clippingPlane, this.clippingPlane.plane);

    let clippingPlaneIndex = 0;
    for (const clippingPlane of globalClippingPlanes) {
      if (clippingPlaneIndex >= this.globalClippingPlanes.length) {
        this.globalClippingPlanes.push(new THREE.Plane());
      }
      planeBSPToTHREEClippingPlane(clippingPlane, this.globalClippingPlanes[clippingPlaneIndex]);
      ++clippingPlaneIndex;
    }
    this.globalClippingPlanes.length = clippingPlaneIndex;

    const group = new THREE.Group();

    const renderMeshes = (currMeshes: THREE.Mesh[], renderOrder: number) => {
      const subgroup = new THREE.Group();

      for (const mesh of currMeshes) {
        // Render back faces incrementing stencil
        const mesh0 = new THREE.Mesh(mesh.geometry, incrementStencilMat);
        mesh0.renderOrder = renderOrder;
        mesh0.position.copy(mesh.position);
        mesh0.rotation.copy(mesh.rotation);
        mesh0.scale.copy(mesh.scale);
        subgroup.add(mesh0);

        (mesh0 as unknown as ExtendedObject3D).ignoreOverrideMaterial = true;
        mesh0.layers.enable(CLIPPING_LAYER);

        // Render front faces decrementing stencil
        const mesh1 = new THREE.Mesh(mesh.geometry, decrementStencilMat);
        mesh1.renderOrder = renderOrder;
        mesh1.position.copy(mesh.position);
        mesh1.rotation.copy(mesh.rotation);
        mesh1.scale.copy(mesh.scale);
        subgroup.add(mesh1);

        (mesh1 as unknown as ExtendedObject3D).ignoreOverrideMaterial = true;
        mesh1.layers.enable(CLIPPING_LAYER);

        group.add(subgroup);
      }
    };

    // render the first block of meshes
    renderMeshes(meshes1, 1);

    // render/clear stencil with the hole meshes
    for (const holeMesh of holeMeshes) {
      const subgroup = new THREE.Group();

      const mesh = new THREE.Mesh(holeMesh.geometry, clearStencilMat);
      mesh.renderOrder = 1.1;
      mesh.position.copy(holeMesh.position);
      mesh.rotation.copy(holeMesh.rotation);
      mesh.scale.copy(holeMesh.scale);
      subgroup.add(mesh);

      (mesh as unknown as ExtendedObject3D).ignoreOverrideMaterial = true;
      mesh.layers.enable(CLIPPING_LAYER);

      group.add(subgroup);
    }

    // render the second block of meshes
    renderMeshes(meshes2, 1.2);

    if (addSectionPoche) {
      this.section = new THREE.Mesh(sectionGeo, crossSectionMaterial);
      (this.section as unknown as ExtendedObject3D).ignoreOverrideMaterial = true;
      this.section.renderOrder = 1.3;
      this.section.layers.set(UI_LAYER);
      this.clippingPlane.plane.coplanarPoint(this.section.position);
      this.section.lookAt(
        this.section.position.x - this.clippingPlane.plane.normal.x,
        this.section.position.y - this.clippingPlane.plane.normal.y,
        this.section.position.z - this.clippingPlane.plane.normal.z,
      );

      group.add(this.section);
    } else {
      this.section = undefined;
    }
    return group;
  }

  setClippingGroup(group: THREE.Object3D) {
    this.clippingGroup = group;
  }

  resetClip() {
    this.clippingPlane.layers.disableAll();
    this.clippingGroup = undefined;
    this.globalClippingPlanes = [];
    return new THREE.Group();
  }

  private onBeforeRender(renderer: THREE.WebGLRenderer, camera: THREE.Camera) {
    this.sceneLightsManager.onBeforeRender(this.scene, camera, this.isRenderingLightProbe);

    // If environment is undefined, we are likely waiting for the first environment map to load.
    // It's important that we don't switch away from the placeholder environment at that point.
    if (this.environment) {
      let targetsForRenderer = environmentTargets.get(renderer);
      if (!targetsForRenderer) {
        targetsForRenderer = new WeakMap();
        environmentTargets.set(renderer, targetsForRenderer);
      }
      let target = targetsForRenderer.get(this.environment);
      if (!target) {
        const pmremGen = getPmremGenerator(renderer);
        // Process both CubeTextures and equirectangular textures through PMREM
        // This generates the mipmaps needed for roughness-based reflections
        target =
          this.environment instanceof THREE.CubeTexture
            ? pmremGen.fromCubemap(this.environment)
            : pmremGen.fromEquirectangular(this.environment);
        targetsForRenderer.set(this.environment, target);
      }
      this.scene.environment = target.texture;
    }

    const isRenderingLighting = this.isRenderingLightProbe || this.isRenderingShadowMap;

    if (this.container && this.container.grid !== null) {
      const overrideMaterialDisablesGrid =
        this.scene.overrideMaterial &&
        this.scene.overrideMaterial instanceof THREE.Material &&
        this.scene.overrideMaterial.userData.disableGrid;
      this.container.grid.visible = !isRenderingLighting && !overrideMaterialDisablesGrid;
    }

    // TODO: It's a bit dirty to set the filteredClippingPlanes here in the onBeforeRender callback.
    // Maybe there's a better way?
    (renderer as ExtendedWebGLRenderer).filteredClippingPlanes = isRenderingLighting ? [] : [this.clippingPlane];
    renderer.clippingPlanes = isRenderingLighting ? [] : this.globalClippingPlanes;

    if (this.levels !== undefined) {
      for (const group of this.scene.children) {
        group.traverse((levelGroup) => {
          if (levelGroup.userData?.levelId !== undefined) {
            levelGroup.visible =
              isRenderingLighting || isLevelGroupVisible(levelGroup, this.selectedLevelId, this.levels);
          }
        });
      }
    }

    if (this.clippingGroup !== undefined) {
      const isRenderingLightProbeOrWireframe = this.isRenderingLightProbe || this.isRenderingWireframe;
      const isPicking =
        !!this.scene.overrideMaterial &&
        this.scene.overrideMaterial instanceof THREE.ShaderMaterial &&
        this.scene.overrideMaterial.fragmentShader === pickingFragmentShader;

      const isolateMode = this.container?.highlightDataTexture?.isolateMode ?? false;
      this.clippingGroup.visible =
        (!this.scene.overrideMaterial || isPicking) &&
        !this.isRenderingShadowMap &&
        !isRenderingLightProbeOrWireframe &&
        !isolateMode;
      if (isPicking) {
        crossSectionMaterial.color.set(0x000000);
      } else {
        crossSectionMaterial.color.set(this.crossSectionMaterialColor);
      }
      if (this.section && this.section.parent === this.clippingGroup) {
        // Update the clipping plane position and orientation from the section object. The section object can be transformed by debug controls.
        this.section.getWorldDirection(tempVector);
        const clippingPlane = planeBSPByPointAndNormal(
          this.section.position.x,
          this.section.position.y,
          this.section.position.z,
          tempVector.x,
          tempVector.y,
          tempVector.z,
        );
        planeBSPToTHREEClippingPlane(clippingPlane, this.clippingPlane.plane);
      }
    }
  }

  private onAfterRender(renderer: THREE.WebGLRenderer) {
    if (this.clippingGroup !== undefined && this.clippingGroup.visible) {
      // We do this here rather than in section.onAfterRender(),
      // since clearing the stencil seems to have some side effects on subsequent rendering of transparent objects on the same frame.
      // That issue was reproduced on GeForce GTX 1080, Chrome on Windows 10.
      renderer.clearStencil();
    }
    renderer.clippingPlanes = [];
  }

  updateShadowMap(renderer: THREE.WebGLRenderer, camera: THREE.Camera) {
    if (
      renderer === this.lastShadowMapRenderer?.deref() &&
      this.lastRenderedShadowGeneration === this.shadowGenerationInternal
    ) {
      return;
    }
    if (!this.sceneLightsManager.directionalLight) {
      return;
    }
    if (!this.sceneLightsManager.directionalLight.castShadow) {
      // It's possible that shadow setup is not yet done, or disabled if we only want to render linework for example.
      return;
    }
    this.lastShadowMapRenderer = new WeakRef(renderer);
    this.lastRenderedShadowGeneration = this.shadowGenerationInternal;
    renderer.shadowMap.needsUpdate = true;
    this.isRenderingShadowMap = true;
    this.onBeforeRender(renderer, camera);
    (renderer as ExtendedWebGLRenderer).renderShadowMap([this.sceneLightsManager.directionalLight], this.scene, camera);
    this.onAfterRender(renderer);
    this.isRenderingShadowMap = false;
  }

  /**
   * Get the direction from the light target to the light position.
   */
  getDirectionalLightDirection(target: THREE.Vector3): void {
    target.setFromMatrixPosition(this.sceneLightsManager.directionalLight.matrixWorld);
    tempVector.setFromMatrixPosition(this.sceneLightsManager.directionalLight.target.matrixWorld);
    target.sub(tempVector);
    target.normalize();
  }

  applyToneMappingToSkyMaterial(halfFloatLinearRenderTarget: boolean, toneMapping: ToneMapping) {
    const toneMappingEnabled = this.sceneLightsManager.toneMappingEnabled && !halfFloatLinearRenderTarget;
    this.applyToneMappingToSkyMaterialInternal(toneMappingEnabled, toneMapping);
  }

  getSceneSharedUniformValues(halfFloatLinearRenderTarget: boolean): SceneSharedUniformValues {
    const toneMappingEnabled = this.sceneLightsManager.toneMappingEnabled && !halfFloatLinearRenderTarget;
    return {
      highlightedIds: this.container?.highlightDataTexture?.texture ?? getEmptyHighlightTexture(),
      radialFogParams: {
        centerX: this.sceneLightsManager.radialFogCenterX,
        centerY: this.sceneLightsManager.radialFogCenterY,
        near: this.sceneLightsManager.radialFogNear,
        far: this.sceneLightsManager.radialFogFar,
      },
      ssfxToneMappingEnabled: toneMappingEnabled,
      toneMappingExposure: this.sceneLightsManager.toneMappingExposure,
      isolateMode: this.container?.highlightDataTexture?.isolateMode ?? false,
    };
  }

  private interruptAsyncProbeUpdate() {
    if (this.asyncProbeUpdater !== undefined) {
      if (this.asyncProbeUpdater.return) {
        this.asyncProbeUpdater.return();
      }
      this.asyncProbeUpdater = undefined;
    }
  }

  private applyToneMappingToSkyMaterialInternal(enabled: boolean, toneMapping: ToneMapping) {
    this.sceneLightsManager.skyMaterial.uniforms.toneMappingExposure.value =
      this.sceneLightsManager.toneMappingExposure;
    this.sceneLightsManager.skyMaterial.uniforms.ssfxToneMappingEnabled.value = enabled;
    assertIsRecordWithKeys(this.sceneLightsManager.skyMaterial.defines, ToneMappingDefineKeys);
    if (setToneMappingDefine(this.sceneLightsManager.skyMaterial.defines as ToneMappingDefines, toneMapping)) {
      this.sceneLightsManager.skyMaterial.needsUpdate = true;
    }
  }

  // Override this to get a callback.

  onBeforeLightProbeUpdate() {}

  lightProbeRenderStep(
    renderer: THREE.WebGLRenderer,
    lightProbeGenerationParams: LightProbeGenerationParams,
    renderAfterEveryProbe: boolean,
  ) {
    (this.scene as ExtendedScene).getUniformsForLayerMask = this.layerSpecificUniforms.getUniformsForLayerMask;
    if (this.asyncProbeUpdater === undefined && this.hasDirtyLightProbes()) {
      this.asyncProbeUpdater = this.updateLightProbesAsync(lightProbeGenerationParams, renderer, renderAfterEveryProbe);
    }
    if (this.asyncProbeUpdater !== undefined) {
      if (this.asyncProbeUpdater.next().done) {
        this.asyncProbeUpdater = undefined;
      }
    }
  }

  *updateLightProbesAsync(
    lightProbeGenerationParams: LightProbeGenerationParams,
    renderer: THREE.WebGLRenderer,
    renderAfterEveryProbe: boolean,
  ) {
    const lightProbeGrids = this.lightProbeGrids;
    if (lightProbeGrids.length === 0) {
      return;
    }

    let totalProbesToUpdate = 0;
    for (let i = 0, len = lightProbeGrids.length; i < len; ++i) {
      if (lightProbeGrids[i].probesNeedUpdate) {
        totalProbesToUpdate += lightProbeGrids[i].getRenderedProbeCount();
      }
    }
    if (totalProbesToUpdate === 0) {
      return;
    }

    this.lightProbeUpdateProgressInternal = 0.0;
    this.onBeforeLightProbeUpdate();

    // It's faster to update the matrices manually once rather than having them updated every time the scene is
    // rendered.
    this.scene.traverse(function (obj) {
      obj.updateMatrixWorld();
    });

    this.layerSpecificUniforms.lightProbeBouncesToUse = 1;
    this.layerSpecificUniforms.useApproximateLightProbeData = true;

    let oldSceneAutoUpdate;

    const onBeforeRenderProbe = (bounceIndex) => {
      oldSceneAutoUpdate = this.scene.matrixWorldAutoUpdate;

      this.isRenderingLightProbeInternal = true;
      this.scene.matrixWorldAutoUpdate = false;
      prepareMaterialsForRender();

      const sharedUniformValues: SharedUniformValues = {
        outputType: SSFXRendererOutput.FINAL_RENDER_WITH_EFFECTS,
        ssfxEnabled: false,

        ssfxToneMappingEnabled: false,
        toneMappingExposure: 1.0,

        highlightedIds: getEmptyHighlightTexture(),

        softShadowCameraFar: SOFT_SHADOW_CAMERA_FAR_DEFAULT,

        lightProbeIrradianceMultiplier: bounceIndex > 0 ? 1.0 : 0.0,
        probeGridNormalOffsetMultiplier: PROBE_GRID_NORMAL_OFFSET_MULTIPLIER_DEFAULT,
        probeGridSmoothBackfaceWeightConstantTerm: PROBE_GRID_SMOOTH_BACKFACE_TERM_CONSTANT_TERM_DEFAULT,

        renderTargetResolution: [1, 1],
        screenSpaceAoMap: null,
        screenSpaceReflectionMap: null,
        screenSpaceShadowMap: null,
        substitutedReflectionMultiplier: 0.0,

        radialFogParams: {
          centerX: this.sceneLightsManager.radialFogCenterX,
          centerY: this.sceneLightsManager.radialFogCenterY,
          near: this.sceneLightsManager.radialFogNear,
          far: this.sceneLightsManager.radialFogFar,
        },

        isolateMode: false,
      };
      MaterialSharedParameterGroup.instance.setSharedUniformValues(sharedUniformValues);
      this.applyToneMappingToSkyMaterialInternal(false, TONE_MAPPING_ALGORITHM);
      (renderer as ExtendedWebGLRenderer).filteredClippingPlanes = [];
      for (let i = 0, len = lightProbeGrids.length; i < len; ++i) {
        lightProbeGrids[i].visible = false;
      }
      this.layerSpecificUniforms.lightProbeBouncesToUse = bounceIndex;
      this.layerSpecificUniforms.useApproximateLightProbeData = false;
      this.layerSpecificUniforms.substituteMissingProbesWithFlatAmbient = false;
    };

    const onAfterRenderProbe = (bounceIndex: number) => {
      this.isRenderingLightProbeInternal = false;
      this.scene.matrixWorldAutoUpdate = oldSceneAutoUpdate;
      if (bounceIndex === 0) {
        this.layerSpecificUniforms.lightProbeBouncesToUse = 1;
        this.layerSpecificUniforms.useApproximateLightProbeData = true;
      } else {
        this.layerSpecificUniforms.lightProbeBouncesToUse = bounceIndex;
        this.layerSpecificUniforms.useApproximateLightProbeData = false;
      }
      this.layerSpecificUniforms.substituteMissingProbesWithFlatAmbient = true;
      if (renderAfterEveryProbe && this.container) {
        this.container.markRenderingNeedsUpdate();
      }
    };

    let totalProbeUpdatesDone = 0;
    for (let bounceIndex = 0; bounceIndex < lightProbeGenerationParams.lightProbeBounces; ++bounceIndex) {
      for (let i = 0, len = lightProbeGrids.length; i < len; ++i) {
        if (!lightProbeGrids[i].probesNeedUpdate) {
          continue;
        }
        const iterator = lightProbeGrids[i].asyncUpdateFromScene({
          renderer,
          scene: this.scene,
          near: lightProbeGenerationParams.lightProbeNear,
          far: lightProbeGenerationParams.lightProbeFar,
          bounceIndex,
          onBeforeRender: onBeforeRenderProbe,
          onAfterRender: onAfterRenderProbe,
          irradianceSaturationMultiplier: this.sceneLightsManager.probeIrradianceSaturationMultiplier,
        });
        let next;
        let gridProbeUpdateCount = 0;
        do {
          yield;
          try {
            next = iterator.next();
          } catch (e) {
            checkWebGLContextLost(renderer, e, "Rendering lighting failed.");
            break;
          }
          gridProbeUpdateCount = next.value;
          this.lightProbeUpdateProgressInternal =
            (totalProbeUpdatesDone + gridProbeUpdateCount) /
            (totalProbesToUpdate * lightProbeGenerationParams.lightProbeBounces);
        } while (!next.done);
        totalProbeUpdatesDone += gridProbeUpdateCount;
        if (bounceIndex === lightProbeGenerationParams.lightProbeBounces - 1) {
          lightProbeGrids[i].probesNeedUpdate = false;
        }
      }
      this.lightProbeUpdateProgressInternal = 1.0;
      this.layerSpecificUniforms.lightProbeBouncesToUse = bounceIndex + 1;
      this.layerSpecificUniforms.useApproximateLightProbeData = false;
      this.container?.markRenderingNeedsUpdate();
    }
  }
}
